/**
 * Shared self-documentation data for the Ion Eval admin dashboard
 * (AdminIonEvalSubtab.jsx + AdminIonEvalCasesSubtab.jsx) — human labels +
 * tooltip copy for the deterministic failure-attribution codes
 * (`eval/metrics/deterministic.py::score_case`) and the suite-aggregate
 * metric keys (`eval_runner.py::_aggregate`), so both the case table and any
 * drill-down show the same wording instead of a raw snake_case key.
 *
 * Keep this in sync with the backend enums it mirrors:
 *   - failure_attribution values: eval_runner.py persists
 *     `Scorecard.failure_attribution` verbatim from `score_case()`.
 *   - summary keys: `eval_runner.py::_aggregate()` return dict.
 */

// code -> {label, tooltip}. One entry per `score_case()` attribution value —
// keep the set in sync with deterministic.py's `attribution = "..."` lines.
export const FAILURE_REASON_INFO = {
  reply_language_mismatch: {
    label: 'Wrong reply language',
    tooltip:
      "The reply was not in the language the case declares in `lang`. Ion's system prompt promises it detects the language of the LATEST user message and answers in it, so this is a user-visible contract break, not a style nit. Note the check grades against the case's `lang` field — the EXPECTED REPLY language — which is not always the prompt's own language (a case may ask in English for a German answer). Detection is a dependency-free function-word profile and deliberately abstains on very short replies, so a mismatch here is a positive finding rather than a guess.",
  },
  loop_stall: {
    label: 'Agent stalled',
    tooltip: 'The agent looped without making progress and the server-side stall detector cut it off (error_code="agent_stalled"). Usually a tool-use loop or a confused planning step.',
  },
  loop_max_iterations: {
    label: 'Hit iteration cap',
    tooltip: 'The agent used up its max tool-call iterations without reaching a final answer (error_code="max_iterations"). May indicate a task that is genuinely too complex for the current step budget, or an inefficient tool-use path.',
  },
  provider_error: {
    label: 'Provider error',
    tooltip: 'Bedrock returned a terminal error (throttling, timeout, service fault) before the turn completed. Usually transient — re-run to confirm before treating it as a model regression.',
  },
  forbidden_tool: {
    label: 'Called a forbidden tool',
    tooltip: 'The agent invoked a tool listed in this case\'s forbid_tools — a hard violation regardless of the final answer\'s quality.',
  },
  did_not_complete: {
    label: 'Did not complete',
    tooltip: 'This case has must_complete=true and the turn did not end cleanly (no end_turn/max_tokens stop, or a terminal error) — the agent never produced its intended final answer.',
  },
  asked_instead_of_answering: {
    label: 'Asked instead of answering',
    tooltip:
      'The agent ended the turn by asking the user a question (ask_user), which hands control back on purpose and leaves stop_reason=tool_use. This case set must_complete=true, so it still fails — but the agent did not crash or truncate. Check the relevant playbook first: if it tells the agent to clarify before sizing (general_selection does, for range/runtime targets), the case is the thing to change, not the model.',
  },
  empty_output: {
    label: 'Empty output',
    tooltip: 'The turn stopped cleanly but produced no final text at all.',
  },
  missing_expected_tool: {
    label: 'Missing expected tool call',
    tooltip: 'Tool-correctness recall < 100% — at least one tool listed in expect_tools (or reference_tools) was never called. Tool SET is graded, not call order.',
  },
  unwanted_output_or_hallucination: {
    label: 'Forbidden content present',
    tooltip: 'The final answer contains a substring listed in output_excludes — often used to catch a hallucinated fact or a value that should not appear.',
  },
  missing_expected_content: {
    label: 'Missing expected content',
    tooltip: 'The final answer is missing a substring listed in output_contains.',
  },
  value_out_of_range: {
    label: 'Value out of range',
    tooltip:
      "No number in the answer lies in the case's expect_value_range. A cited [[V:N]] chip in the range's unit is checked first, then a number with a unit in the prose (decimal comma understood for de/fr). The case drawer shows the range and every number it found, converted to the range's unit.",
  },
  cost_cap_turn: {
    label: 'Ion Next turn cost cap',
    tooltip:
      "Native engines only: the turn hit Ion Next's per-turn spend cap and was cut off, so there is no complete answer to grade (no judge call is made). The run went on with the next case. Usually a runaway tool or code-execution loop; re-run the case before reading it as a quality result.",
  },
  cost_cap_conversation: {
    label: 'Ion Next conversation cost cap',
    tooltip:
      "Native engines only: the turn hit Ion Next's per-conversation spend cap and was cut off (no judge call). The run went on with the next case. The daily cap is different: it stops the whole run as partial instead of failing one case.",
  },
  refusal_mismatch: {
    label: 'Refusal mismatch',
    tooltip:
      'expect_refusal does not match what the answer did. true = the case expects Ion to decline (out of scope, a person, a future prediction, a prompt-extraction attempt) and it answered anyway; false = the case expects a real answer and Ion declined. Detected deterministically from decline phrasing (de + en) in the opening of the answer, suppressed when the answer carries a [[V:N]] citation or is very long. A lexical heuristic — the case metrics list the exact phrase that matched.',
  },
  citation_mismatch: {
    label: 'Citation mismatch',
    tooltip: 'expect_citation does not match whether the answer actually carried a verified-value citation ([[V:N]] chip). true = case expects at least one citation; false = case expects none.',
  },
  inefficiency: {
    label: 'Inefficient (warning only)',
    tooltip: 'Tool-call count exceeded this case\'s max_tool_calls_expected budget. This is a WARNING, not a hard fail — it never flips passed to false on its own.',
  },
};

export function failureReasonLabel(code) {
  if (!code) return '—';
  return FAILURE_REASON_INFO[code]?.label || code;
}

export function failureReasonTooltip(code) {
  return code ? FAILURE_REASON_INFO[code]?.tooltip || null : null;
}

// Suite-aggregate summary_json keys (`eval_runner.py::_aggregate`) + the two
// per-run list/detail fields (pass@k / pass^k / task completion) that reuse
// the same wording — one lookup table for both surfaces.
export const METRIC_INFO = {
  pass_rate_by_lang: {
    label: 'Pass rate by language',
    tooltip:
      'Overall pass rate split by the case `lang` field, so a regression confined to one language is visible instead of being averaged away by the majority language. The corpus is mostly English, which means a German-only or French-only break moves the overall number barely at all.',
  },
  reply_language_match_rate: {
    label: 'Reply language match',
    tooltip:
      'Share of GRADED attempts whose reply was in the language the case declares. Graded, not total: the check abstains on replies too short to profile reliably, so this is measured against `reply_language_graded_attempts`, not against all attempts.',
  },
  reply_language_graded_attempts: {
    label: 'Reply language graded',
    tooltip:
      'How many attempts the language check actually scored. Lower than total attempts because cases with no `lang` set, and replies too terse to profile, deliberately abstain rather than guess — an abstention passes, so treat a low number here as low coverage, not as success.',
  },
  n_cases: {
    label: 'Cases',
    tooltip: 'Number of distinct eval cases included in this run (before multiplying by repeats).',
  },
  n_attempts: {
    label: 'Attempts',
    tooltip: 'Total individual attempts executed = cases × repeats. The denominator for overall pass rate and the operational (cost/latency) metrics.',
  },
  n_attempts_passed: {
    label: 'Attempts passed',
    tooltip: 'How many of the cases × repeats attempts fully passed every applicable assertion.',
  },
  overall_pass_rate: {
    label: 'Overall pass rate',
    tooltip: 'Share of ALL attempts (case × repeat) that fully passed — the true correctness rate across the whole run. Distinct from pass@k (per-case, any repeat), pass^k (per-case, all repeats), and task completion (clean termination only, not correctness).',
  },
  task_completion_rate: {
    label: 'Clean-termination rate',
    tooltip: 'Share of ALL attempts that ended cleanly (end_turn/max_tokens, no terminal error) with a non-empty answer. This is a LIVENESS signal, NOT correctness — a cleanly-finished WRONG answer still counts here. Use Overall pass rate / pass@k for correctness. (A polite refusal also "completes"; whether it was correct is an expect_refusal/judge concern.)',
  },
  tool_precision_mean: {
    label: 'Tool precision (mean)',
    tooltip: 'Mean of |expected ∩ actual| / |actual| across all attempts with an expected-tools set — did the agent avoid calling tools it didn\'t need?',
  },
  tool_recall_mean: {
    label: 'Tool recall (mean)',
    tooltip: 'Mean of |expected ∩ actual| / |expected| across all attempts with an expected-tools set — did the agent call everything it needed?',
  },
  arg_validity_lower_bound_mean: {
    label: 'Argument validity (lower bound, mean)',
    tooltip: '1 − tool-error-rate, averaged across all attempts. A LOWER BOUND only: it catches schema/validation errors, not semantic argument correctness (which needs a judge).',
  },
  cost_per_attempt_usd: {
    label: 'Cost per attempt (USD)',
    tooltip: 'Actual Bedrock spend for this run ÷ number of attempts. The average bill for one case run.',
  },
  cost_per_passed_case_usd: {
    label: 'Cost per passed attempt (USD)',
    tooltip: 'Actual Bedrock spend ÷ number of PASSED attempts — the cost of each successful outcome. Blank when nothing passed. A single efficiency number combining quality and price.',
  },
  latency_ms_p50: {
    label: 'Latency p50 (ms)',
    tooltip: 'Median end-to-end wall-clock time per attempt (full agent turn, all tool round-trips included).',
  },
  latency_ms_p95: {
    label: 'Latency p95 (ms)',
    tooltip: '95th-percentile end-to-end wall-clock time per attempt — the slow-tail latency a real user would occasionally hit.',
  },
  stall_rate: {
    label: 'Stall rate',
    tooltip: 'Share of ALL repeats (not just first) where the agent stalled in a tool-use loop (error_code="agent_stalled").',
  },
  max_iterations_rate: {
    label: 'Max-iterations rate',
    tooltip: 'Share of all repeats that hit the iteration cap before finishing.',
  },
  provider_failure_rate: {
    label: 'Provider failure rate',
    tooltip: 'Share of all repeats that ended in a terminal Bedrock/provider error.',
  },
  value_in_range_rate: {
    label: 'Value in range',
    tooltip:
      'Share of the attempts with an expect_value_range whose answer stated a number inside that range (from a cited chip, else from a number with a unit in the prose). Blank when no case of the run carries a range.',
  },
  value_in_range_chip_rate: {
    label: 'Value in range, from a cited chip',
    tooltip:
      'The part of Value in range that rests on a cited [[V:N]] chip rather than on a number in the prose: the answer is backed by a tool result, not just stated.',
  },
  value_in_range_graded_attempts: {
    label: 'Value-in-range graded attempts',
    tooltip: 'How many attempts the range check scored, i.e. the attempts of cases that carry an expect_value_range.',
  },
  pass_at_k_rate: {
    label: 'pass@k',
    tooltip: 'Share of cases where AT LEAST ONE of the k repeats passed (k = the run\'s Repeats setting). The optimistic view: "can the model get this right at all?"',
  },
  pass_hat_k_rate: {
    label: 'pass^k (all-of-k)',
    tooltip: 'Share of cases where ALL k repeats passed. The pessimistic/reliability view: "does the model get this right EVERY time?" Always ≤ pass@k for the same k.',
  },
};

export function metricLabel(key) {
  return METRIC_INFO[key]?.label || key;
}

export function metricTooltip(key) {
  return METRIC_INFO[key]?.tooltip || null;
}

// Field-level help for the run-detail grid + run-configurator + case
// editor — anything that isn't a suite-aggregate metric or failure code.
export const FIELD_INFO = {
  repeats: 'How many independent times each case runs (with the SAME prompt). Repeats >1 is what makes pass@k vs pass^k meaningful — a single repeat can\'t distinguish "reliably right" from "got lucky once". 3-5 is a reasonable default; cost and wall time scale linearly with it.',
  judge: 'Whether any DeepEval grader is configured. When on, EACH case gets one extra LLM call (the "judge") that scores subjective criteria (hallucination, rubric adherence) — separate cost and latency from the always-on, free, deterministic scorer.',
  temperature: 'Sampling temperature passed to the model for this run only (blank = provider default). Lower values are more reproducible run-to-run; this does not affect production chat, only the eval run\'s own calls.',
  judgeModel: 'The model used to grade DeepEval criteria (hallucination / GEval rubric), independent of the model(s) under test. Each grader invocation is a separate, billed LLM call.',
  graders: {
    tool_correctness: 'DeepEval LLM-graded tool-selection quality — a softer, judge-based companion to the always-on deterministic precision/recall.',
    hallucination: 'DeepEval hallucination/faithfulness check against this case\'s Reference answer field. Requires Reference answer to be filled in, or the grader has nothing to compare against.',
    geval_rubric: 'DeepEval GEval scoring against this case\'s Rubric text field (a plain-language grading rubric). Requires Rubric text to be filled in on the CASE, not the run.',
  },
  engines:
    'Which agent runs the cases. Classic is the customer Ion (ChatService on Bedrock, the models below). The native engines are Ion Next on Claude Opus 5.5 / Sonnet 5.5: native:ion with its full prompt and knowledge, native:baseline with the bare baseline profile. Each engine runs every picked model it supports; more than one engine × model makes a comparison group. Native spend also counts toward the Ion Next daily cap.',
  mechanisms: {
    tools_enabled: 'When off, the agent runs with NO tools registered at all (a pure-text baseline) — web search and knowledge are forced off too, since both depend on tool calls.',
    web_search_enabled: 'When off, web access is removed: the classic web_search tool, and on the native engines the web search and web fetch server tools. Other tools (cell data, knowledge, calculations) stay available.',
    knowledge_enabled: 'When off, only get_knowledge is excluded — tests whether the agent still answers correctly without consulting the curated knowledge base.',
  },
  baselineRun: 'Pick a prior run to diff this one against. The Suite aggregates block then shows a Δ next to every numeric metric (this run\'s value minus the baseline\'s) so a regression/improvement is visible at a glance.',
  suite: 'Optional named group of cases (the Suite field on each case). Selecting one adds every ACTIVE case tagged with that suite to the run, unioned with any cases individually checked below.',
  gitSha: 'The backend commit the run executed against — lets you correlate a pass-rate change with a specific code change.',
  promptVersion: 'A free-text label for the system-prompt version in effect during this run, when set at trigger time.',
};

// Tri-state ("don't care" / true / false) case-field help — shared by
// CaseEditorModal's selects.
export const CASE_FIELD_INFO = {
  caseName: 'Unique natural key for this case (snake_case). Cannot be changed after creation — exports/imports and eval-run results key off this name, not a database id.',
  prompt: 'The exact user message sent to Ion when this case runs. Retained verbatim as curated eval-suite material — this is what every check below evaluates against.',
  expectTools: 'Comma-separated tool names the agent SHOULD call (set-based — order doesn\'t matter). Leave blank if this case has no specific tool expectation. Example: get_cell_metric, plot_curves',
  forbidTools: 'Comma-separated tool names that are a HARD FAIL if called, regardless of the final answer. Example: web_search',
  expectRefusal: '"don\'t care" skips this check entirely. "must refuse" / "must NOT refuse" are checked by the DETERMINISTIC scorer (no judge involved, no grader to configure): it looks for a decline phrase — German and English are both always checked — in the opening ~400 characters of the answer, and treats the answer as substantive (i.e. NOT a refusal) if it carries a [[V:N]] citation or runs past ~200 words, so a hedged-but-real answer is not mistaken for a refusal. A mismatch fails the case with refusal_mismatch. It is a lexical heuristic, not a proof: the per-case metrics show which phrase matched so you can check the verdict.',
  expectCitation: '"don\'t care" always passes. "must cite" requires at least one [[V:N]] verified-value chip in the final answer; "must not cite" requires none. Checked by the deterministic scorer (no judge needed).',
  mustComplete: '"don\'t care" skips the completion check. "true" fails the case if the turn doesn\'t end cleanly (no clean stop = did_not_complete). Most cases that expect a real answer should set this to true.',
  outputContains: 'Comma-separated substrings that MUST appear (case-insensitive) somewhere in the final answer. Example: 280 Ah, LFP',
  outputExcludes: 'Comma-separated substrings that must NOT appear — use to catch a known hallucination or a value the agent should never state unprompted.',
  confidence: 'How much you trust this case\'s expectations (informational only — not used in scoring). Useful when auditing suite quality or triaging failures.',
  suite: 'A short suite tag (e.g. core_data, sales_flow) used to group cases so a run configurator can select "everything in suite X" instead of picking cases one by one.',
  tags: 'Free-form labels for filtering/organizing cases in this dashboard. Not read by the runner.',
  rubricText: 'Plain-language grading rubric read by the GEval DeepEval grader (only used when that grader is enabled on a run). Has no effect unless a run configured with geval_rubric selects this case.',
  referenceAnswer: 'The gold/expected answer text, read by the Hallucination and GEval DeepEval graders for comparison. Has no effect on the deterministic scorer.',
  notes: 'Free-text context for other maintainers — why this case exists, known caveats, links to the feedback item that prompted it, etc.',
};

// Section order of `EvalCaseHoverCard` — declared here so the card and any
// future reuse (e.g. the cases subtab) stay in the same reading order.
export const CASE_HOVER_SECTIONS = [
  'identity',
  'prompt',
  'expectations',
  'graders',
  'metadata',
  'notes',
];

// Which DeepEval grader a case can actually be scored by. A run that enables a
// grader whose per-case input field is empty grades NOTHING for that case —
// silently — so the picker/hover card has to say which ones are live.
export const GRADER_REQUIREMENTS = {
  geval_rubric: { field: 'rubricText', label: 'needs GEval rubric grader' },
  hallucination: { field: 'referenceAnswer', label: 'needs hallucination grader' },
};

/**
 * Pure, render-free summary of everything an eval case asserts.
 * Shared by the hover card; kept here so it is unit-testable without a DOM.
 */
export function describeCaseExpectations(c) {
  const expectTools = c?.expectTools || [];
  const forbidTools = c?.forbidTools || [];
  const referenceTools = c?.referenceTools || [];
  const outputContainsCount = (c?.outputContains || []).length;
  const outputExcludesCount = (c?.outputExcludes || []).length;

  // Tri-states are rendered ONLY when non-null: null means "don't care" and the
  // deterministic scorer skips the check entirely, so showing it would imply an
  // assertion that does not exist.
  const triStates = [];
  if (c?.expectRefusal === true) triStates.push('must refuse');
  else if (c?.expectRefusal === false) triStates.push('must NOT refuse');
  if (c?.expectCitation === true) triStates.push('must cite');
  else if (c?.expectCitation === false) triStates.push('must not cite');
  if (c?.mustComplete === true) triStates.push('must complete');
  else if (c?.mustComplete === false) triStates.push('completion not required');

  const graders = Object.entries(GRADER_REQUIREMENTS)
    .filter(([, req]) => Boolean(c?.[req.field]))
    .map(([id, req]) => ({ id, label: req.label }));

  return {
    expectTools,
    forbidTools,
    referenceTools,
    outputContainsCount,
    outputExcludesCount,
    triStates,
    graders,
    hasExpectations:
      expectTools.length > 0 ||
      forbidTools.length > 0 ||
      outputContainsCount > 0 ||
      outputExcludesCount > 0 ||
      triStates.length > 0,
  };
}
